import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BellRing, Send } from "lucide-react";

const Label = ({ children }) => <div className="mb-1 font-oswald text-[11px] uppercase tracking-widest text-gray-400">{children}</div>;
const Input = (p) => <input {...p} className={`w-full rounded-sm border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:border-[#1f63c9] focus:outline-none ${p.className || ""}`} />;

export function WatchCard() {
  const [w, setW] = useState(null);
  const [form, setForm] = useState({ email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/stream-watch/config").then((r) => { setW(r.data); setForm({ email: r.data.email, phone: r.data.phone }); }).catch(() => {}); }, []);

  const save = async (body, msg) => {
    try { const { data } = await api.put("/stream-watch/config", body); setW(data); if (msg) toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/stream-watch/test"); toast.success(`Test email sent to ${data.to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };

  if (!w) return null;
  const stateTone = w.state === "online" ? "text-emerald-300" : w.state === "offline" ? "text-red-400" : "text-gray-400";
  return (
    <div className="glass rounded-sm border border-amber-400/40 p-6" data-testid="watch-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BellRing className="h-5 w-5 text-amber-300" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Offline Alerts</div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-200">
          <input type="checkbox" data-testid="watch-enabled" checked={w.enabled} onChange={(e) => save({ enabled: e.target.checked }, e.target.checked ? "Alerts on" : "Alerts off")} className="h-4 w-4 accent-[#1f63c9]" />
          {w.enabled ? "On" : "Off"}
        </label>
      </div>
      <p className="mt-1 text-sm text-gray-300">If the broadcast page stops sending heartbeats for longer than the threshold, you get an email — and another when it comes back. Reminders every 6 hours while it's still down.</p>

      <div className="mt-4 grid gap-4 md:grid-cols-[2fr_2fr_1fr]">
        <div>
          <Label>Email {w.email_configured ? <span className="text-emerald-400">· ready</span> : <span className="text-amber-300">· email service not configured</span>}</Label>
          <Input data-testid="watch-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} onBlur={() => form.email !== w.email && save({ email: form.email }, "Email saved")} placeholder="you@example.com" />
        </div>
        <div>
          <Label>Mobile for texts {w.sms_configured ? <span className="text-emerald-400">· ready</span> : <span className="text-gray-500">· add Twilio keys to enable</span>}</Label>
          <Input data-testid="watch-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} onBlur={() => form.phone !== w.phone && save({ phone: form.phone }, "Phone saved")} placeholder="+1 501 555 0100" />
        </div>
        <div>
          <Label>Alert after</Label>
          <select data-testid="watch-threshold" value={w.threshold_min} onChange={(e) => save({ threshold_min: Number(e.target.value) }, "Threshold saved")} className="w-full rounded-sm border border-white/15 bg-black/40 px-3 py-2 text-sm text-white focus:border-[#1f63c9] focus:outline-none">
            {[2, 3, 5, 10, 15, 30].map((n) => <option key={n} value={n}>{n} minutes</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-sm border border-white/10 bg-black/30 px-4 py-3">
        <div className="text-sm">
          <span className="font-oswald text-[11px] uppercase tracking-widest text-gray-400">Watchdog sees the stream as </span>
          <span className={`font-oswald text-sm font-bold uppercase ${stateTone}`} data-testid="watch-state">{w.state}</span>
          {w.state_since && <span className="ml-2 text-xs text-gray-500">since {new Date(w.state_since).toLocaleString()}</span>}
          {w.last_alert_at && <div className="mt-0.5 text-xs text-gray-500" data-testid="watch-last-alert">Last alert: {w.last_alert_kind} · {new Date(w.last_alert_at).toLocaleString()}</div>}
          {w.last_error && <div className="mt-0.5 text-xs text-red-300">Last send error: {w.last_error}</div>}
        </div>
        <button type="button" data-testid="watch-test-btn" onClick={test} disabled={busy || !w.email} className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] disabled:opacity-50">
          <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send test email"}
        </button>
      </div>
    </div>
  );
}
